/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-06-30
 */

export interface DynamicObject {
    [key: string]: string | number | boolean;
}

export interface IQueryListComms {
    query: string;
    schema: string;
}

export interface IQueryListModal { //diego test
    modalTitle?: string;
    modalSubtitle?: string;
    modalProptBy?: string;
    tableName?: string;
    tableLineQuery?: string;
    modalAction?: string;
    modalHasViewing?: boolean;
    queryData?: IQuery, 
    processData?: (result: any) => JSX.Element
}

export interface QueryListProperties {
    listTitle: string;
    recordTitle: string;
    tableName: string;
    editTableName: string;
    modal?: IQueryListModal;
    editHasCompoundKey: boolean;
    hasIntegrationStatusLegend?: boolean;
    integrationstatusLegend?:{
        status: string,
        color: string,
        description: string
    }[];
    errorCategoryName?: string
    join: IJoin[];
    select: ISelect[],
    where: IWhere[],
    orderBy: IOrderBy[],
    groupBy: string[], //IGroupBy[]
} 

export interface IQueryColumnDetails {
    
    aggregate: string | null,
    joinName: string | null,
    columnName: string,
    fullColumnName: string,
    displayColumnName: string,
    fullDisplayColumnName: string,
    tableName: string,
    isControl: boolean,
    isPk: boolean,
    isRequired: boolean | null,
    isMainTable: boolean,
    isStoredInExtra: boolean,
    columnSeq: number | null,
    tableSeq: number | null,
    datatype: string
}

export interface IColumnDetails {
    name: string;
    datatype: string;
    displayName: string;
    seq: number;
    required: boolean;
    pk: boolean;
}

export interface ISchema {
    name: string;
    idColumn: string;
    extraColumn: string;
    createDateColumn: string;
    createUserColumn: string;
    updateDateColumn: string;
    updateUserColumn: string;
    touchDateColumn: string;
    touchUserColumn: string;
    logStatusColumn: string;
    logNumberColumn: string;
    dataColumns: IColumnDetails[],
    extraColumns: IColumnDetails[],
    controlColumns: IColumnDetails[]
}

export enum CastType {
    BIGINT = "BIGINT", 
    BOOLEAN = "BOOLEAN", 
    DATE = "DATE",
    DECIMAL = "DECIMAL",
    FLOAT = "FLOAT", 
    DOUBLE = "DOUBLE",
    INTEGER = "INTEGER",
    JSONB = "JSONB", 
    NUMERIC = "NUMERIC", 
    STRING = "STRING",
    TIME = "TIME", 
    TIMESTAMP = "TIMESTAMP", 
    TIMESTAMPTZ = "TIMESTAMPTZ",    
    UUID = "UUID",     
    VARCHAR = "VARCHAR"
}

export interface IJoin {
    relationship: string; 
    joinType: string;
}

export interface ISelect {
    fullColumnName: string; 
    aggregate: string | null;
}

// export interface IGroupBy {
//     fullColumnName: string;
// }

export interface IWhere {
    fullColumnName: string 
    value: any;
    operator: string; 
    cast?: CastType | null;
    not?: boolean;
}

export interface IOrderBy {
    fullColumnName: string; 
    direction: string; 
    cast?: CastType | null;
}

export interface IFrontEndOnly {
    listTitle: string;
    recordTitle: string;
    editTableName: string;
    editHasCompoundKey: boolean;
}

export interface IIdSelect {
    sql?: string;
    params?: any[];
}

export interface IQuery {
    tableName: string;
    select: ISelect[];
    groupBy: string[]; //IGroupBy[]
    join: IJoin[]; // Defaults to {relationship: 'some_table_name', joinType: 'LEFT'}
    orderBy: IOrderBy[]; // Defaults to {column: 'some_column_name', direction: 'ASC', cast: 'STRING'} 
    where: IWhere[]; // Default to {fullColumnName: string, value: {}, operator: 'EQ', cast: 'STRING'}
    mainTableOnly: boolean;
    includeExtraColumns: boolean;
    includeControlColumns: boolean;
    modifiedAfter: string | null;     // where greatest(rootTable.create_date, rootTable.update_date) > modifiedAfter
    touchedBefore: string | null;      // where rootTable.touch_date < touchedBefore
    isDirty: boolean | null;
    pageNum?: number;
    pageSize?: number;
    frontEndOnly: IFrontEndOnly | null; // Component properties only, server never works with this. 
    idSelect?: IIdSelect | null
}

//New Formates Of Quering Data
export interface IQuerySelectData {
  appCode: string,
  selectCode: string,
  where?: string;
  orderBy?: string;
  pageNum?: number;
  pageSize?: number;
  numRecords?: number;
  params?: Record<string, string>;
}

export interface IQueryTableData {
  tableName: string;
  where?: string;
  orderBy?: string;
  pageNum?: number;
  pageSize?: number;
  numRecords?: number;
  params?:  Record<string, string>;
}

export interface INotificationSummary {
    category: string;
    description: string;
    last_encountered: string;
    total: number
}

export interface IUploadFilePreviewItem {
  itemCode: string;
  barcode: string;
  name: string;
  count: number;
}

export interface DamagedReturnRow {
  doc_num: string;
  category: string;
  from_whs: string;
  comments: string | null;
}

export interface IQueryExtract {
    sheetName: string;
    queryCreate: IQuery; 
}


export interface IItemBase {
  id: string,
  created: string | null, 
  updated: string | null,
  dirty: boolean,
  touched: string, 
  name: string,
  description: string | null,
  group_name: string | null, 
  opts: string[], 
  product_base_assets: { id: string, url: string }[] | null, 
  product_variants: { id: string, is_closed: boolean, sku: string, name: string}[] | null, 
  is_closed: boolean | null, 
  channels: { id: string, is_enabled: boolean, code: string}[]| null
}

export interface IItemBaseVariant {
  id: string;
  created: string | null, 
  updated: string | null,
  dirty: boolean,
  touched: string, 
  base_product_id: string, 
  opts: Record<string, string>,
  product_base_assets: { id: string, url: string }[] | null,
  price: number,
  name: string, 
  sku: string, 
  is_closed: boolean | null,
  locations: { id: string, code: string, name: string, in_stock: number} [] 
  channels: { id: string, is_enabled: boolean, code: string}[]| null
}

export interface ITableItemChannel {
    id: string,
    code: string,
    channel_data: string | null,
    product_export_script: string,
    base_url: string,
    description: string | null,
    channel_type: string,
    color_code: string
}
